import { ConsentService } from '../internal/services/consent-service';
import { SessionService } from '../internal/services/session-service';
import { ProviderService } from '../internal/services/provider-service';
import { ChatInstance } from '../internal/chat-instance/chat-instance';
import { UserInfo } from '../internal/user-info/user-info';
import { ConnectionState, ChatState } from './models/events-models';
import { GatewayProvider } from '../providers/gateway/gateway-provider';
export class EnGenChat {
    constructor(config) {
        this.session = {
            chatInstance: new ChatInstance(),
            userInfo: new UserInfo(),
            config,
        };
        this.consentService = new ConsentService(config);
        this.providerService = new ProviderService(config);
        this.sessionService = new SessionService(config);
        this.onStatusChange = this.session.chatInstance.observableEvents().onStatusChange$;
        this.onFeatureUpdate = this.session.chatInstance.observableEvents().onFeatureUpdate$;
        this.onParticipantUpdate = this.session.chatInstance.observableEvents().onParticipantUpdate$;
        this.onIsTyping = this.session.chatInstance.observableEvents().onIsTyping$;
        this.onNewMessage = this.session.chatInstance.observableEvents().onNewMessage$;
        this.onError = this.session.chatInstance.observableEvents().onError$;
        this.onNotification = this.session.chatInstance.observableEvents().onNotification$;
    }
    async startChat(token, providerId, requestParameters) {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (connectionState === ConnectionState.CONNECTING ||
            connectionState === ConnectionState.CONNECTED ||
            connectionState === ConnectionState.RECONNECTING) {
            throw { code: 'INVALID_CHAT_STATUS', message: 'Invalid connection state.' };
        }
        if (chatStatus.state !== ChatState.READY) {
            throw { code: 'CHAT_STATUS_NOT_READY', message: 'Invalid chat state.' };
        }
        const availability = await this.providerService.getAvailability(token, providerId);
        if (!availability.isAvailable) {
            this.session.chatInstance.emitStatusChange('', availability.provider, ConnectionState.NO_CONNECTION, {
                state: ChatState.UNAVAILABLE,
            });
            throw { code: 'PROVIDER_UNAVAILABLE', message: availability.unavailableReason };
        }
        this.chatProvider = new GatewayProvider(this.session);
        return this.chatProvider.startChat(token, providerId, availability.platformSettings, requestParameters);
    }
    closeChat(token) {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (connectionState !== ConnectionState.CONNECTING &&
            connectionState !== ConnectionState.CONNECTED &&
            connectionState !== ConnectionState.RECONNECTING) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'Invalid connection state.' });
        }
        if (chatStatus.state !== ChatState.ACTIVE &&
            chatStatus.state !== ChatState.ON_HOLD &&
            chatStatus.state !== ChatState.TRANSFERRING) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'Invalid chat state.' });
        }
        return this.chatProvider.closeChat(token);
    }
    sendMessage(token, text) {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (connectionState !== ConnectionState.CONNECTED) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'No active connection.' });
        }
        if (chatStatus.state !== ChatState.ACTIVE) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'No active chat session.' });
        }
        return this.chatProvider.sendMessage(token, text);
    }
    sendEvent(token, buttonContent) {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (connectionState !== ConnectionState.CONNECTED) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'No active connection.' });
        }
        if (chatStatus.state !== ChatState.ACTIVE) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'No active chat session.' });
        }
        return this.chatProvider.sendEvent(token, buttonContent);
    }
    sendIsTyping() {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (connectionState !== ConnectionState.CONNECTED || chatStatus.state !== ChatState.ACTIVE) {
            return;
        }
        this.chatProvider.sendIsTyping();
    }
    async getAvailability(token, providerId) {
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        const validChatStates = [ChatState.UNAVAILABLE, ChatState.READY, ChatState.COMPLETE];
        const validConnectionStates = [ConnectionState.NO_CONNECTION, ConnectionState.DISCONNECTED];
        if ((connectionState && !validConnectionStates.includes(connectionState)) ||
            (chatStatus && !validChatStates.includes(chatStatus.state))) {
            throw { code: 'INVALID_CHAT_STATUS' };
        }
        const provider = await this.providerService.getAvailability(token, providerId);
        if (provider.isAvailable) {
            this.session.chatInstance.emitStatusChange('', provider.provider, ConnectionState.NO_CONNECTION, {
                state: ChatState.READY,
            });
        }
        else {
            this.session.chatInstance.emitStatusChange('', provider.provider, ConnectionState.NO_CONNECTION, {
                state: ChatState.UNAVAILABLE,
            });
        }
        return provider;
    }
    getProviders(token) {
        return this.providerService.getProviders(token);
    }
    getConsent(token, consentType) {
        return this.consentService.getConsent(token, consentType);
    }
    setConsent(token, consentInfo) {
        return this.consentService.setConsent(token, consentInfo);
    }
    getSessions(token) {
        return this.sessionService.getSessions(token);
    }
    getTranscript(token, sessionId) {
        return this.sessionService.getTranscript(token, sessionId);
    }
}
//# sourceMappingURL=chat.js.map